import { call, put, race, select, take, takeLatest } from "redux-saga/effects";

import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import type { Activity } from "@mapmycustomers/shared/types/entity";
import type {
  FiWordCloudRequest,
  FiWordCloudResponse,
} from "@mapmycustomers/shared/types/entity/fieldInsight";
import type Organization from "@mapmycustomers/shared/types/Organization";
import type Team from "@mapmycustomers/shared/types/Team";
import type ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import type { PlatformFilterCondition } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import getActivityRelatedEntity from "@app/scene/dashboard/utils/getActivityRelatedEntity";
import noteFilterTerms from "@app/scene/dashboard/utils/noteFilterTerms";
import scopeToFilter from "@app/scene/dashboard/utils/scopeToFilter";
import stripMarkup from "@app/scene/dashboard/utils/stripMarkup";
import termPattern from "@app/scene/dashboard/utils/termPattern";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam/selectors";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import { formatDate } from "@app/util/formatters";

import {
  cancelWordCloudNotesExport,
  EXPORT_PAGE_SIZE,
  exportWordCloudNotes,
  fetchWordCloudData,
  fetchWordCloudNotes,
  WordCloudNote,
} from "./actions";

type NotesPageRequest = Omit<
  ReturnType<typeof fetchWordCloudNotes>["payload"],
  "callback" | "failureCallback" | "word"
>;

interface NotesPage {
  hasMore: boolean;
  notes: WordCloudNote[];
}

function* onFetchWordCloudData({ payload }: ReturnType<typeof fetchWordCloudData>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { endDate, startDate } =
      payload.customRange ?? getDatesRange(payload.dateRange.range, payload.dateRange.subRange);

    const request: FiWordCloudRequest = {
      endDate: formatDate(endDate, "yyyy-MM-dd"),
      startDate: formatDate(startDate, "yyyy-MM-dd"),
      ...(payload.scope ? scopeToFilter(payload.scope) : {}),
    };

    const response: FiWordCloudResponse = yield callApi(
      "executeFieldInsightsWordCloud",
      orgId,
      request
    );
    yield call(payload.callback, response.data);
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

function* fetchNotesByActivityIds(
  payload: NotesPageRequest,
  orgId: Organization["id"],
  activityIds: Activity["id"][]
) {
  if (activityIds.length === 0) {
    return { hasMore: false, notes: [] };
  }

  const conditions: PlatformFilterCondition[] = [
    { id: { [PlatformFilterOperator.CONTAINS]: activityIds } },
  ];
  if (payload.query) {
    conditions.push({
      $or: [
        { note: { [PlatformFilterOperator.CONTAINS]: payload.query } },
        { name: { [PlatformFilterOperator.CONTAINS]: payload.query } },
      ],
    });
  }

  const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
    $filters: { $and: conditions },
    $limit: payload.limit,
    $offset: payload.offset,
    $order: "-completedAt",
  });

  const notes: WordCloudNote[] = response.data.map((activity) => ({
    id: activity.id,
    activityType: activity.crmActivityType,
    completedAt: activity.completedAt,
    name: activity.name,
    note: stripMarkup(activity.note ?? "").normalize("NFC"),
    relatedEntity: getActivityRelatedEntity(activity),
  }));
  return { hasMore: payload.offset + response.data.length < response.total, notes };
}

function* fetchNotesPage(payload: NotesPageRequest, orgId: Organization["id"]) {
  if (payload.activityIds) {
    const page: NotesPage = yield call(
      fetchNotesByActivityIds,
      payload,
      orgId,
      payload.activityIds
    );
    return page;
  }

  const { endDate, startDate } =
    payload.customRange ?? getDatesRange(payload.dateRange.range, payload.dateRange.subRange);

  // Same scope narrowing the word-cloud endpoint applies server-side.
  const { teamId, userId } = scopeToFilter(payload.scope ?? {});
  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const assigneeIds = userId
    ? [userId]
    : teamId
      ? (teams?.find(({ id }) => id === teamId)?.users.map(({ id }) => id) ?? [])
      : undefined;
  if (assigneeIds?.length === 0) {
    return { hasMore: false, notes: [] };
  }

  // A counted form is a word sequence, so the filter narrows on its words, not the phrase.
  const termConditions: PlatformFilterCondition[] = noteFilterTerms(payload.matchedForms).map(
    (term) => ({ note: { [PlatformFilterOperator.CONTAINS]: term } })
  );

  // Only a term with no searchable letters at all, which matches no note.
  if (termConditions.length === 0) {
    return { hasMore: false, notes: [] };
  }

  const conditions: PlatformFilterCondition[] = [
    { $or: termConditions },
    { completedAt: { $gte: startDate.toISOString(), $lte: endDate.toISOString() } },
  ];
  if (assigneeIds) {
    conditions.push({ assigneeId: { [PlatformFilterOperator.CONTAINS]: assigneeIds } });
  }
  if (payload.query) {
    conditions.push({
      $or: [
        { note: { [PlatformFilterOperator.CONTAINS]: payload.query } },
        { name: { [PlatformFilterOperator.CONTAINS]: payload.query } },
      ],
    });
  }

  const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
    $filters: { $and: conditions },
    $limit: payload.limit,
    $offset: payload.offset,
    $order: "-completedAt",
  });

  // `$contains` matched single words; drop the notes holding one but no counted sequence.
  // De-marked-up and normalized once here, so the excerpt and the highlighter index into the
  // same string the pattern was tested against — the counted forms are NFC, and a raw note need
  // not be. Markup is removed for the reason the server removes it before counting: a tag or
  // character reference between two words tokenizes to a word of its own, so the pattern would
  // reject a note the count includes.
  // A wider entry outranks the term and consumes the span it covers, exactly as it does
  // server-side — so a note whose only "patient" sits inside the counted "patient outcomes" is
  // not one of this term's notes, and listing it would overshoot the count.
  const countedForm = new RegExp(termPattern(payload.matchedForms, payload.widerForms), "giu");
  const notes: WordCloudNote[] = response.data
    .map((activity) => ({ activity, note: stripMarkup(activity.note ?? "").normalize("NFC") }))
    .filter(({ note }) => [...note.matchAll(countedForm)].some(({ groups }) => groups?.term))
    .map(({ activity, note }) => ({
      id: activity.id,
      activityType: activity.crmActivityType,
      completedAt: activity.completedAt,
      name: activity.name,
      note,
      relatedEntity: getActivityRelatedEntity(activity),
    }));
  // Raw page, not `notes` — the whole-word filter can empty a full page.
  return { hasMore: response.data.length === payload.limit, notes };
}

function* onFetchWordCloudNotes({ payload }: ReturnType<typeof fetchWordCloudNotes>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { hasMore, notes }: NotesPage = yield call(fetchNotesPage, payload, orgId);
    yield call(payload.callback, notes, hasMore);
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

function* exportNotes({ payload }: ReturnType<typeof exportWordCloudNotes>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const notesById = new Map<WordCloudNote["id"], WordCloudNote>();
    let offset = 0;
    let hasMore = true;
    while (hasMore) {
      const page: NotesPage = yield call(
        fetchNotesPage,
        { ...payload, limit: EXPORT_PAGE_SIZE, offset },
        orgId
      );
      page.notes.forEach((note) => notesById.set(note.id, note));
      hasMore = page.hasMore;
      offset += EXPORT_PAGE_SIZE;
      yield call(payload.progressCallback, notesById.size);
    }
    yield call(payload.callback, [...notesById.values()]);
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

function* onExportWordCloudNotes(action: ReturnType<typeof exportWordCloudNotes>) {
  yield race({ cancel: take(cancelWordCloudNotesExport), export: call(exportNotes, action) });
}

export function* wordCloudSagas() {
  yield takeLatest(fetchWordCloudData, onFetchWordCloudData);
  yield takeLatest(fetchWordCloudNotes, onFetchWordCloudNotes);
  yield takeLatest(exportWordCloudNotes, onExportWordCloudNotes);
}
